import React, { useState, useEffect, useCallback } from 'react';
import { 
  BookOpen, Plus, Search, Trash2, Edit2, Upload, Download, 
  ChevronRight, ChevronDown, CheckCircle, Clock, Teacher, 
  BarChart2, Save, X, Loader2, MoreVertical, AlertCircle,
  FileText, Calendar, CopyPlus, AlertTriangle, FileSpreadsheet
} from 'lucide-react';
import apiClient from '@/utils/apiClient';
import downloadFile from '@/utils/fileDownloader';
import { useAuth } from '@/App';
import { toast } from 'sonner';
const AcademicManagement = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Download Template State
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadClass, setDownloadClass] = useState('');
  const [downloadCategories, setDownloadCategories] = useState(['half_yearly', 'annual']);
  const [downloadSubjects, setDownloadSubjects] = useState(['Mathematics', 'Science', 'English']);
  const [newDownloadCategory, setNewDownloadCategory] = useState('');
  const [newDownloadSubject, setNewDownloadSubject] = useState('');

  // Import Template State
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importValidation, setImportValidation] = useState(null);
  const [importError, setImportError] = useState(null);

  const toggleDownloadCategory = (cat) => {
    setDownloadCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const addCustomDownloadCategory = () => {
    if (newDownloadCategory.trim()) {
      const cat = newDownloadCategory.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
      if (cat && !downloadCategories.includes(cat)) {
        setDownloadCategories(prev => [...prev, cat]);
      }
      setNewDownloadCategory('');
    }
  };

  const toggleDownloadSubject = (sub) => {
    setDownloadSubjects(prev =>
      prev.includes(sub) ? prev.filter(s => s !== sub) : [...prev, sub]
    );
  };

  const addCustomDownloadSubject = () => {
    if (newDownloadSubject.trim()) {
      const sub = newDownloadSubject.trim();
      if (sub && !downloadSubjects.includes(sub)) {
        setDownloadSubjects(prev => [...prev, sub]);
      }
      setNewDownloadSubject('');
    }
  };

  // Syllabus Categories inside Editor Modal
  const [modalCategories, setModalCategories] = useState(['half_yearly', 'annual']);
  const [activeSubjectFilter, setActiveSubjectFilter] = useState('all');

  // Custom Delete Confirmation Modal State
  const [deleteConfirmationPlan, setDeleteConfirmationPlan] = useState(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const plansPerPage = 6;

  // Form State
  const [formData, setFormData] = useState({
    class_name: '',
    subjects: []
  });

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/v1/admin/academic/plans');
      setPlans(response.data.plans || []);
    } catch (error) {
      toast.error('Failed to fetch academic plans');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClasses = useCallback(async () => {
    if (!user?.hostel_id) return;
    try {
      setLoadingClasses(true);
      const response = await apiClient.get(`/v1/admin/classes/?hostel_id=${user.hostel_id}`);
      setAvailableClasses(response.data || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
      toast.error('Could not load classes list');
    } finally {
      setLoadingClasses(false);
    }
  }, [user?.hostel_id]);

  useEffect(() => {
    fetchPlans();
    fetchClasses();
  }, [fetchPlans, fetchClasses]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleCreateNew = () => {
    setEditingPlan(null);
    setModalCategories(['half_yearly', 'annual']);
    setActiveSubjectFilter('all');
    setFormData({
      class_name: '',
      subjects: [
        {
          _key: Math.random().toString(36).substring(2, 9),
          subject_name: '',
          teacher_name: '',
          chapters: [
            { _key: Math.random().toString(36).substring(2, 9), name: '', timeline: '', category_weightages: { 'half_yearly': 0 } }
          ]
        }
      ]
    });
    setShowForm(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    
    // Gather all existing categories from the syllabus plan
    const uniqueCats = new Set(['half_yearly', 'annual']);
    if (plan && plan.subjects) {
      plan.subjects.forEach(sub => {
        if (sub.chapters) {
          sub.chapters.forEach(chap => {
            if (chap.exam_category) {
              uniqueCats.add(chap.exam_category);
            }
            if (chap.exam_category_2) {
              uniqueCats.add(chap.exam_category_2);
            }
            if (chap.category_weightages) {
              Object.keys(chap.category_weightages).forEach(c => {
                if (c) uniqueCats.add(c);
              });
            }
          });
        }
      });
    }
    setModalCategories(Array.from(uniqueCats));
    setActiveSubjectFilter('all');

    setFormData({
      class_name: plan.class_name,
      subjects: plan.subjects.map(sub => ({
        ...sub,
        _key: sub._key || sub.subject_name || Math.random().toString(36).substring(2, 9),
        chapters: sub.chapters.map(chap => {
          const cat_weights = { ...chap.category_weightages };
          if (Object.keys(cat_weights).length === 0) {
            if (chap.exam_category) {
              cat_weights[chap.exam_category] = chap.weightage || 0;
            }
            if (chap.exam_category_2) {
              cat_weights[chap.exam_category_2] = chap.weightage_2 || 0;
            }
          }
          return {
            ...chap,
            _key: chap.id || chap._key || Math.random().toString(36).substring(2, 9),
            category_weightages: cat_weights
          };
        })
      }))
    });
    setShowForm(true);
  };

  const handleAddSubject = () => {
    setActiveSubjectFilter('all');
    setFormData(prev => ({
      ...prev,
      subjects: [
        {
          _key: Math.random().toString(36).substring(2, 9),
          subject_name: '',
          teacher_name: '',
          chapters: [
            { _key: Math.random().toString(36).substring(2, 9), name: '', timeline: '', category_weightages: {} }
          ]
        },
        ...prev.subjects
      ]
    }));
  };

  const handleRemoveSubject = (sIdx) => {
    setActiveSubjectFilter('all');
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.filter((_, idx) => idx !== sIdx)
    }));
  };

  const handleAddChapter = (sIdx) => {
    setFormData(prev => {
      const newSubjects = prev.subjects.map((sub, idx) => {
        if (idx === sIdx) {
          return {
            ...sub,
            chapters: [
              ...(sub.chapters || []),
              {
                _key: Math.random().toString(36).substring(2, 9),
                name: '',
                timeline: '',
                category_weightages: {}
              }
            ]
          };
        }
        return sub;
      });
      return { ...prev, subjects: newSubjects };
    });
  };

  // Insert a duplicate of the chapter immediately after cIdx so the user can
  // assign the same chapter to a different exam category/weightage/timeline.
  const handleAddChapterVariant = (sIdx, cIdx) => {
    setFormData(prev => {
      const newSubjects = prev.subjects.map((sub, idx) => {
        if (idx !== sIdx) return sub;
        const chapters = [...(sub.chapters || [])];
        const source = chapters[cIdx];
        const variant = {
          _key: Math.random().toString(36).substring(2, 9),
          name: source.name,
          timeline: source.timeline || '',
          category_weightages: { ...(source.category_weightages || {}) },
          status: 'pending',
          completed_at: null
        };
        chapters.splice(cIdx + 1, 0, variant);
        return { ...sub, chapters };
      });
      return { ...prev, subjects: newSubjects };
    });
    toast.success('New category entry added — set the weightage and category below.');
  };

  const handleRemoveChapter = (sIdx, cIdx) => {
    setFormData(prev => {
      const newSubjects = prev.subjects.map((sub, idx) => {
        if (idx !== sIdx) return sub;
        return {
          ...sub,
          chapters: sub.chapters.filter((_, cIdx2) => cIdx2 !== cIdx)
        };
      });
      return { ...prev, subjects: newSubjects };
    });
  };

  const handleUpdateCategoryWeight = (sIdx, cIdx, cat, weight) => {
    setFormData(prev => {
      const newSubjects = prev.subjects.map((sub, idx) => {
        if (idx !== sIdx) return sub;
        const newChapters = sub.chapters.map((chap, cIdx2) => {
          if (cIdx2 !== cIdx) return chap;
          return {
            ...chap,
            category_weightages: {
              ...chap.category_weightages,
              [cat]: weight
            }
          };
        });
        return { ...sub, chapters: newChapters };
      });
      return { ...prev, subjects: newSubjects };
    });
  };

  const handleRenameCategory = (sIdx, cIdx, oldCat, newCat) => {
    setFormData(prev => {
      const newSubjects = prev.subjects.map((sub, idx) => {
        if (idx !== sIdx) return sub;
        const newChapters = sub.chapters.map((chap, cIdx2) => {
          if (cIdx2 !== cIdx) return chap;
          const cat_weights = { ...chap.category_weightages };
          const weight = cat_weights[oldCat] || 0;
          delete cat_weights[oldCat];
          cat_weights[newCat] = weight;
          return {
            ...chap,
            category_weightages: cat_weights
          };
        });
        return { ...sub, chapters: newChapters };
      });
      return { ...prev, subjects: newSubjects };
    });
  };

  const handleDeleteCategoryWeight = (sIdx, cIdx, cat) => {
    setFormData(prev => {
      const newSubjects = prev.subjects.map((sub, idx) => {
        if (idx !== sIdx) return sub;
        const newChapters = sub.chapters.map((chap, cIdx2) => {
          if (cIdx2 !== cIdx) return chap;
          const cat_weights = { ...chap.category_weightages };
          delete cat_weights[cat];
          return {
            ...chap,
            category_weightages: cat_weights
          };
        });
        return { ...sub, chapters: newChapters };
      });
      return { ...prev, subjects: newSubjects };
    });
  };

  const handleAddCategoryWeight = (sIdx, cIdx) => {
    setFormData(prev => {
      const newSubjects = prev.subjects.map((sub, idx) => {
        if (idx !== sIdx) return sub;
        const newChapters = sub.chapters.map((chap, cIdx2) => {
          if (cIdx2 !== cIdx) return chap;
          const cat_weights = { ...chap.category_weightages };
          const unused = modalCategories.find(c => cat_weights[c] === undefined) || `exam_${Object.keys(cat_weights).length + 1}`;
          cat_weights[unused] = 0;
          return {
            ...chap,
            category_weightages: cat_weights
          };
        });
        return { ...sub, chapters: newChapters };
      });
      return { ...prev, subjects: newSubjects };
    });
  };

  const handleSubjectChange = (sIdx, field, value) => {
    setFormData(prev => {
      const newSubjects = prev.subjects.map((sub, idx) => {
        if (idx !== sIdx) return sub;
        return {
          ...sub,
          [field]: value
        };
      });
      return { ...prev, subjects: newSubjects };
    });
  };

  const handleChapterChange = (sIdx, cIdx, field, value) => {
    setFormData(prev => {
      const newSubjects = prev.subjects.map((sub, idx) => {
        if (idx !== sIdx) return sub;
        const newChapters = sub.chapters.map((chap, cIdx2) => {
          if (cIdx2 !== cIdx) return chap;
          return {
            ...chap,
            [field]: value
          };
        });
        return { ...sub, chapters: newChapters };
      });
      return { ...prev, subjects: newSubjects };
    });
  };

  const handleSave = async () => {
    if (!formData.class_name.trim()) {
      toast.error('Please enter a class name');
      return;
    }

    if (formData.subjects.length === 0) {
      toast.error('Please add at least one subject');
      return;
    }

    // Prepare payload by ensuring legacy fields are populated from category_weightages
    const payload = {
      ...formData,
      subjects: formData.subjects.map(sub => ({
        ...sub,
        chapters: sub.chapters.map(chap => {
          const cat_weights = chap.category_weightages || {};
          const keys = Object.keys(cat_weights);
          const firstCat = keys[0] || 'general';
          const firstWeight = keys[0] !== undefined ? (cat_weights[firstCat] || 0) : 0;
          const secondCat = keys[1] || null;
          const secondWeight = secondCat ? cat_weights[secondCat] : null;

          return {
            ...chap,
            exam_category: firstCat,
            weightage: firstWeight,
            exam_category_2: secondCat,
            weightage_2: secondWeight,
            category_weightages: cat_weights
          };
        })
      }))
    };

    setSaving(true);
    try {
      if (editingPlan) {
        await apiClient.put(`/v1/admin/academic/plans/${editingPlan.id}`, payload);
        toast.success('Syllabus updated successfully!');
      } else {
        await apiClient.post('/v1/admin/academic/plans', payload);
        toast.success('Syllabus created successfully!');
      }
      setShowForm(false);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save academic plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (planId) => {
    try {
      await apiClient.delete(`/v1/admin/academic/plans/${planId}`);
      toast.success('Plan deleted successfully');
      fetchPlans();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const handleToggleChapter = async (plan, subjectName, chapterId, currentStatus, sIdx, cIdx) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await apiClient.patch(`/v1/admin/academic/plans/${plan.id}/chapter-status`, {
        subject_name: subjectName,
        chapter_id: chapterId,
        status: newStatus
      });
      
      // Update local formData state so the UI updates inside the modal immediately!
      setFormData(prev => {
        const newSubjects = [...prev.subjects];
        if (newSubjects[sIdx] && newSubjects[sIdx].chapters && newSubjects[sIdx].chapters[cIdx]) {
          newSubjects[sIdx].chapters[cIdx].status = newStatus;
        }
        return { ...prev, subjects: newSubjects };
      });

      fetchPlans();
      toast.success(`Chapter marked as ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update chapter status');
    }
  };
  const handleCategoryChange = (sIdx, cIdx, val) => {
    if (val === 'custom_option_trigger') {
      const customName = window.prompt("Enter custom exam category name:");
      if (customName && customName.trim()) {
        const trimmed = customName.trim();
        
        // 1. Add to available categories if not already there
        setModalCategories(prev => {
          if (!prev.includes(trimmed)) {
            return [...prev, trimmed];
          }
          return prev;
        });
        
        // 2. Assign to the target chapter
        handleChapterChange(sIdx, cIdx, 'exam_category', trimmed);
        toast.success(`Category "${trimmed}" added and applied!`);
      } else {
        // Fallback to default half_yearly if prompt was cancelled
        handleChapterChange(sIdx, cIdx, 'exam_category', 'half_yearly');
      }
    } else {
      handleChapterChange(sIdx, cIdx, 'exam_category', val);
    }
  };

  const handleDownloadTemplate = async () => {
    if (downloadSubjects.length === 0) {
      toast.error('Please select or add at least one subject');
      setShowDownloadModal(false);
      return;
    }

    try {
      const params = new URLSearchParams();
      if (downloadClass) {
        params.append('class_name', downloadClass);
      }
      
      if (downloadCategories && downloadCategories.length > 0) {
        params.append('categories', downloadCategories.join(','));
      } else {
        params.append('categories', 'none');
      }
      params.append('subjects', downloadSubjects.join(','));
      
      const response = await apiClient.get(`/v1/admin/academic/template?${params.toString()}`, { responseType: 'blob' });
      
      // Extract filename from response headers if available
      let filename = 'academic_plan_template.xlsx';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/["']/g, '');
        }
      } else {
        // Fallback filename matching the backend
        let suffix = '';
        if (downloadClass) suffix += `_${downloadClass.replace(/[^a-zA-Z0-9]/g, '_')}`;
        if (downloadCategories.length > 0) suffix += `_${downloadCategories[0].replace(/[^a-zA-Z0-9]/g, '_')}`;
        filename = `academic_plan_template${suffix}.xlsx`;
      }

      await downloadFile(response.data, filename, response.headers?.['content-type']);
      setShowDownloadModal(false);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImportFileChange = async (file) => {
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setImportError('Only Excel files (.xlsx, .xls) are allowed.');
      setImportFile(null);
      setImportValidation(null);
      return;
    }
    setImportFile(file);
    setImportError(null);
    setImportValidation(null);
    setImportLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.post('/v1/admin/academic/import?commit=false', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data) {
        setImportValidation(response.data);
        if (!response.data.success) {
          setImportError(response.data.errors?.[0] || 'Validation failed.');
        }
      }
    } catch (err) {
      console.error(err);
      setImportError(err.response?.data?.detail || 'Failed to validate the Excel file.');
    } finally {
      setImportLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!importFile) return;
    setImportLoading(true);
    setImportError(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await apiClient.post('/v1/admin/academic/import?commit=true', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.success) {
        toast.success(response.data.message || 'Academic plan imported successfully!');
        setShowImportModal(false);
        fetchPlans(); // Refresh the list
      } else {
        setImportError(response.data?.errors?.[0] || 'Import failed.');
      }
    } catch (err) {
      console.error(err);
      setImportError(err.response?.data?.detail || 'Failed to import the Excel file.');
    } finally {
      setImportLoading(false);
    }
  };

  const calculateProgress = (subjects) => {
    if (!subjects || subjects.length === 0) return 0;
    
    let totalWeightage = 0;
    let completedWeightage = 0;
    
    subjects.forEach(sub => {
      sub.chapters.forEach(chap => {
        totalWeightage += (chap.weightage || 0);
        if (chap.status === 'completed') {
          completedWeightage += (chap.weightage || 0);
        }
      });
    });
    
    if (totalWeightage === 0) {
      // Fallback to count if no weightage specified
      let totalCount = 0;
      let completedCount = 0;
      subjects.forEach(sub => {
        totalCount += sub.chapters.length;
        completedCount += sub.chapters.filter(c => c.status === 'completed').length;
      });
      return totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100);
    }
    
    return Math.round((completedWeightage / totalWeightage) * 100);
  };

  const filteredPlans = plans.filter(p => 
    p.class_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredPlans.length / plansPerPage);
  const indexOfLastPlan = currentPage * plansPerPage;
  const indexOfFirstPlan = indexOfLastPlan - plansPerPage;
  const currentPlans = filteredPlans.slice(indexOfFirstPlan, indexOfLastPlan);

  const isDuplicatePlan = !editingPlan && formData.class_name && plans.some(p => p.class_name.toLowerCase() === formData.class_name.toLowerCase());

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <BookOpen className="w-8 h-8 text-blue-600" />
            Academic Syllabus Management
          </h1>
          <p className="text-gray-500">Track and manage subject-wise progress for all classes</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <button 
            onClick={() => {
              setDownloadClass(availableClasses[0]?.name || "");
              setDownloadCategories(['half_yearly', 'annual']);
              setDownloadSubjects(['Mathematics', 'Science', 'English']);
              setNewDownloadCategory('');
              setNewDownloadSubject('');
              setShowDownloadModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            Template
          </button>
          <button 
            onClick={() => {
              setImportFile(null);
              setImportValidation(null);
              setImportError(null);
              setShowImportModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          >
            <Upload className="w-4 h-4" />
            Import
          </button>
          <button 
            onClick={handleCreateNew}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
          >
            <Plus className="w-4 h-4" />
            Add Class Plan
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white p-5 rounded-2xl border shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 font-bold text-xl">
            {plans.length}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Total Classes</p>
            <p className="text-xl font-bold text-gray-800">Syllabus Plans</p>
          </div>
        </div>
        <div className="bg-white p-5 rounded-2xl border shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600">
            <BarChart2 className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Average Progress</p>
            <p className="text-xl font-bold text-gray-800">
              {plans.length > 0 ? Math.round(plans.reduce((acc, p) => acc + calculateProgress(p.subjects), 0) / plans.length) : 0}%
            </p>
          </div>
        </div>
        <div className="bg-white p-5 rounded-2xl border shadow-sm relative overflow-hidden flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600">
            <Search className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <input 
              type="text" 
              placeholder="Search class..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-transparent border-none focus:ring-0 text-gray-800 placeholder-gray-400 font-medium"
            />
          </div>
        </div>
      </div>

      {/* Plans List */}
      {loading && plans.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-dashed border-gray-300">
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin mb-4" />
          <p className="text-gray-400 font-medium">Loading academic data...</p>
        </div>
      ) : filteredPlans.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-dashed border-gray-300">
          <BookOpen className="w-16 h-16 text-gray-200 mb-4" />
          <h3 className="text-lg font-bold text-gray-600">No syllabus plans found</h3>
          <p className="text-gray-400 mb-6">Start by importing from Excel or adding a new class.</p>
          <button onClick={handleCreateNew} className="text-blue-600 font-bold hover:underline">Add New Class Plan</button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentPlans.map(plan => {
              const progress = calculateProgress(plan.subjects);
              return (
                <div key={plan.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden">
                  {/* Plan Card Header */}
                  <div className="p-6 border-b border-gray-50 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center justify-between mb-4">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full uppercase tracking-wider">
                        {plan.class_name}
                      </span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleEdit(plan)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteConfirmationPlan(plan)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-end justify-between mb-2">
                      <h3 className="text-xl font-bold text-gray-800">{plan.subjects.length} Subjects</h3>
                      <span className="text-2xl font-black text-blue-600">{progress}%</span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full transition-all duration-1000" 
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  {/* Plan Content - Subjects Preview */}
                  <div className="p-6 bg-white space-y-4">
                    {plan.subjects.slice(0, 3).map(sub => {
                      const subChapters = sub.chapters.length;
                      const completed = sub.chapters.filter(c => c.status === 'completed').length;
                      const subProgress = subChapters > 0 ? Math.round((completed / subChapters) * 100) : 0;
                      
                      return (
                        <div key={sub.subject_name} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-bold text-gray-700">{sub.subject_name}</span>
                            <span className="text-xs text-gray-400 font-medium">{completed}/{subChapters} Chapters</span>
                          </div>
                          <div className="w-full h-1 bg-gray-50 rounded-full overflow-hidden">
                            <div className="h-full bg-green-400 rounded-full" style={{ width: `${subProgress}%` }} />
                          </div>
                        </div>
                      );
                    })}
                    
                    {plan.subjects.length > 3 && (
                      <p className="text-xs text-center text-gray-400 font-medium pt-2">
                        + {plan.subjects.length - 3} more subjects
                      </p>
                    )}
                    
                    <button 
                      onClick={() => handleEdit(plan)}
                      className="w-full mt-4 py-2.5 bg-gray-50 text-gray-700 font-bold rounded-xl hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center gap-2"
                    >
                      Manage Syllabus
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-2xl border shadow-sm">
              <p className="text-sm font-medium text-gray-500">
                Showing <span className="font-bold text-gray-700">{indexOfFirstPlan + 1}</span> to{' '}
                <span className="font-bold text-gray-700">{Math.min(indexOfLastPlan, filteredPlans.length)}</span> of{' '}
                <span className="font-bold text-gray-700">{filteredPlans.length}</span> classes
              </p>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all ${
                    currentPage === 1 
                      ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' 
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                      currentPage === page 
                        ? 'bg-blue-600 text-white shadow-sm shadow-blue-100' 
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition-all ${
                    currentPage === totalPages 
                      ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' 
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Editor Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl scale-in">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingPlan ? `Edit Syllabus - ${editingPlan.class_name}` : 'Create New Class Plan'}
                </h2>
                <p className="text-sm text-gray-500">Define subjects, teachers, and chapters progress</p>
              </div>
              <button 
                onClick={() => setShowForm(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-8 bg-gray-50/30">
              <div className="max-w-4xl mx-auto space-y-8">
                {/* Basic Info */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-black text-gray-400 uppercase tracking-widest mb-3">Select Class</label>
                      {editingPlan ? (
                        <div className="px-5 py-3 rounded-xl bg-gray-50 border border-gray-200 text-lg font-bold text-gray-700">
                          {formData.class_name}
                        </div>
                      ) : (
                        <select 
                          value={formData.class_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, class_name: e.target.value }))}
                          className="w-full px-5 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-lg font-bold bg-white"
                        >
                          <option value="">Select a class...</option>
                          {availableClasses.map(cls => (
                            <option key={cls.id} value={cls.name}>
                              {cls.name}
                            </option>
                          ))}
                        </select>
                      )}
                      {availableClasses.length === 0 && !loadingClasses && !editingPlan && (
                        <p className="text-xs text-amber-600 mt-2 font-medium">
                          No classes found in management. Please create classes first.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-black text-gray-400 uppercase tracking-widest mb-3">Select Subject (Filter View)</label>
                      <select 
                        value={activeSubjectFilter}
                        onChange={(e) => setActiveSubjectFilter(e.target.value)}
                        className="w-full px-5 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-lg font-bold bg-white text-gray-700 cursor-pointer"
                      >
                        <option value="all">✨ Show All Subjects ({formData.subjects.length})</option>
                        {formData.subjects.map((sub, idx) => (
                          <option key={sub._key || idx} value={sub._key || idx.toString()}>
                            📚 {sub.subject_name || `Unnamed Subject (${idx + 1})`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Warning Alert if Duplicate Plan */}
                  {isDuplicatePlan && (
                    <div className="mt-6 p-5 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-4 animate-slide-down">
                      <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center text-red-600 flex-shrink-0">
                        <AlertCircle className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-base font-bold text-red-800">Syllabus Plan Already Exists!</h4>
                        <p className="text-xs text-red-700 mt-1 leading-relaxed">
                          An academic syllabus plan has already been created for <strong>{formData.class_name}</strong>. 
                          To prevent duplicate plans and maintain a single source of truth, you cannot create a new plan for this class.
                        </p>
                        <div className="mt-3 flex flex-col gap-1.5 text-xs text-red-700 font-semibold bg-red-100/50 p-3 rounded-lg border border-red-100">
                          <p>💡 To proceed, please close this modal and:</p>
                          <ul className="list-disc list-inside space-y-1 pl-2 font-medium">
                            <li>Find the <strong>{formData.class_name}</strong> card on the dashboard and click <strong className="text-blue-700">Manage Syllabus</strong> to update it.</li>
                            <li>Or, click the <strong className="text-red-700">Delete icon</strong> on its card to remove the existing plan first.</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Subjects List */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-gray-700">Subjects & Chapters</h3>
                    <button 
                      onClick={handleAddSubject}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 font-bold rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Subject
                    </button>
                  </div>

                  {formData.subjects.map((sub, sIdx) => {
                    if (activeSubjectFilter !== 'all' && activeSubjectFilter !== (sub._key || sIdx.toString())) {
                      return null;
                    }
                    return (
                      <div key={sub._key || sIdx} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        {/* Subject Header */}
                        <div className="p-6 bg-gray-50/50 border-b border-gray-100 flex flex-col md:flex-row gap-4 items-center">
                          <div className="flex-1 w-full md:w-auto">
                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Subject Name</label>
                            <input 
                              type="text"
                              value={sub.subject_name}
                              onChange={(e) => handleSubjectChange(sIdx, 'subject_name', e.target.value)}
                              placeholder="e.g. Mathematics"
                              className="w-full bg-white px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-400 outline-none font-bold"
                            />
                          </div>
                          <div className="flex-1 w-full md:w-auto">
                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Teacher Name</label>
                            <input 
                              type="text"
                              value={sub.teacher_name}
                              onChange={(e) => handleSubjectChange(sIdx, 'teacher_name', e.target.value)}
                              placeholder="e.g. Mr. Sharma"
                              className="w-full bg-white px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-400 outline-none font-medium"
                            />
                          </div>
                        <div className="pt-4">
                          <button 
                            onClick={() => handleRemoveSubject(sIdx)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      {/* Chapters Grid */}
                      <div className="p-6 space-y-4">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest">Chapters Breakdown</label>
                          <button 
                            onClick={() => handleAddChapter(sIdx)}
                            className="text-xs text-blue-600 font-bold flex items-center gap-1 hover:underline"
                          >
                            <Plus className="w-3 h-3" />
                            Add Chapter
                          </button>
                        </div>

                        <div className="space-y-3">
                          {sub.chapters.map((chap, cIdx) => (
                            <div key={chap._key || cIdx} className="flex flex-col md:flex-row items-start md:items-center gap-3 p-4 bg-gray-50 rounded-xl group relative">
                              <div className="flex-1 w-full">
                                <input 
                                  type="text"
                                  value={chap.name}
                                  onChange={(e) => handleChapterChange(sIdx, cIdx, 'name', e.target.value)}
                                  placeholder="Chapter name..."
                                  className="w-full bg-transparent border-none focus:ring-0 font-bold text-gray-700 p-0"
                                />
                              </div>
                              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                                <div className="flex flex-col">
                                  <label className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter">Timeline (Optional)</label>
                                  <input 
                                    type="text"
                                    value={chap.timeline}
                                    onChange={(e) => handleChapterChange(sIdx, cIdx, 'timeline', e.target.value)}
                                    placeholder="e.g. May W1"
                                    className="w-24 bg-white border border-gray-200 rounded px-2 py-0.5 text-xs font-medium"
                                  />
                                </div>

                                <div className="flex flex-col border-l pl-3 border-gray-200 gap-1 w-full md:w-auto">
                                  <label className="text-[8px] font-bold text-gray-400 uppercase tracking-tighter">Exam Categories & Weightages (Optional)</label>
                                  <div className="flex flex-col gap-1.5">
                                    {Object.entries(chap.category_weightages || {}).length === 0 ? (
                                      <div className="flex items-center gap-1.5 py-0.5">
                                        <span className="text-[10px] text-gray-400 italic">None (Optional)</span>
                                        <button
                                          type="button"
                                          onClick={() => handleAddCategoryWeight(sIdx, cIdx)}
                                          className="text-[10px] text-blue-600 hover:text-blue-700 font-semibold underline ml-1"
                                        >
                                          + Add
                                        </button>
                                      </div>
                                    ) : (
                                      Object.entries(chap.category_weightages || {}).map(([cat, weight]) => (
                                        <div key={cat} className="flex items-center gap-1.5">
                                          <select
                                            value={cat}
                                            onChange={(e) => {
                                              const newCat = e.target.value;
                                              if (newCat === 'custom_option_trigger') {
                                                const customName = window.prompt("Enter custom exam category name:");
                                                if (customName && customName.trim()) {
                                                  const trimmed = customName.trim();
                                                  setModalCategories(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
                                                  handleRenameCategory(sIdx, cIdx, cat, trimmed);
                                                }
                                              } else if (newCat && newCat !== cat) {
                                                handleRenameCategory(sIdx, cIdx, cat, newCat);
                                              }
                                            }}
                                            className="w-28 bg-white border border-gray-200 rounded px-1.5 py-0.5 text-[9px] font-bold outline-none focus:ring-1 focus:ring-blue-400 truncate cursor-pointer"
                                          >
                                            {modalCategories.map(c => (
                                              <option key={c} value={c}>
                                                {c === 'half_yearly' ? 'Half-Yearly' : c === 'annual' ? 'Annual' : c}
                                              </option>
                                            ))}
                                            <option value="custom_option_trigger">Custom...</option>
                                          </select>

                                          <input 
                                            type="text"
                                            value={weight || ''}
                                            onChange={(e) => {
                                              const val = e.target.value.replace(/[^0-9]/g, '');
                                              handleUpdateCategoryWeight(sIdx, cIdx, cat, val === '' ? 0 : parseInt(val, 10) || 0);
                                            }}
                                            className="w-12 bg-white border border-gray-200 rounded px-1.5 py-0.5 text-[10px] font-medium text-gray-700 outline-none focus:ring-1 focus:ring-blue-400"
                                            placeholder="0"
                                          />
                                          <span className="text-[10px] text-gray-400">%</span>

                                          <button
                                            type="button"
                                            onClick={() => handleDeleteCategoryWeight(sIdx, cIdx, cat)}
                                            className="p-0.5 text-red-400 hover:text-red-600 rounded transition-colors"
                                            title="Remove category"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-1 px-2 border-l border-gray-200">
                                  {editingPlan && (
                                    <button
                                      onClick={() => handleToggleChapter(editingPlan, sub.subject_name, chap.id, chap.status, sIdx, cIdx)}
                                      className={`p-1.5 rounded-lg transition-all ${
                                        chap.status === 'completed' 
                                          ? 'bg-green-100 text-green-600' 
                                          : 'bg-white text-gray-300 border border-gray-200 hover:text-green-500'
                                      }`}
                                      title={chap.status === 'completed' ? 'Completed' : 'Mark as completed'}
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleAddCategoryWeight(sIdx, cIdx);
                                      toast.success('New category entry added inside the chapter card — select the category and set its weightage.');
                                    }}
                                    className="p-1.5 rounded-lg bg-white border border-gray-200 text-gray-400 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-all"
                                    title="Add another category/weightage entry for this chapter"
                                  >
                                    <CopyPlus className="w-4 h-4" />
                                  </button>
                                </div>

                                <button 
                                  onClick={() => handleRemoveChapter(sIdx, cIdx)}
                                  className="p-1.5 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-100 flex items-center justify-end gap-3 bg-white">
              <button 
                onClick={() => setShowForm(false)}
                className="px-6 py-2 text-gray-600 font-bold hover:bg-gray-50 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSave}
                disabled={saving || isDuplicatePlan}
                className={`px-8 py-2 font-bold rounded-xl transition-all shadow-md flex items-center gap-2 ${
                  isDuplicatePlan 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                {editingPlan ? 'Update Syllabus' : 'Create Syllabus'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Template Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden flex flex-col shadow-2xl scale-in">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white">
              <div>
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <Download className="w-5 h-5 text-blue-600" />
                  Download Syllabus Template
                </h2>
                <p className="text-xs text-gray-500 mt-1">Configure subjects, exam categories, and prefill options for your template</p>
              </div>
              <button 
                onClick={() => setShowDownloadModal(false)}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 bg-gray-50/30 space-y-5 overflow-y-auto max-h-[70vh]">
              {/* Class Selection */}
              <div className="space-y-2">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest">
                  Class Name (Prefilled)
                </label>
                <select 
                  value={downloadClass}
                  onChange={(e) => setDownloadClass(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none font-semibold text-gray-700 bg-white"
                >
                  <option value="">-- Generic Template (No Class) --</option>
                  {availableClasses.map(cls => (
                    <option key={cls.id} value={cls.name}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subjects Selection */}
              <div className="space-y-2">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-widest">
                  Configure Subjects Dropdown
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {['Mathematics', 'Science', 'English', 'Social Studies', 'Hindi', 'Computer Science'].map(sub => {
                    const isSelected = downloadSubjects.includes(sub);
                    return (
                      <button
                        key={sub}
                        type="button"
                        onClick={() => toggleDownloadSubject(sub)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                          isSelected 
                            ? 'bg-blue-50 border-blue-200 text-blue-600 shadow-sm' 
                            : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {sub}
                      </button>
                    );
                  })}
                </div>
                
                {/* Custom Subject Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newDownloadSubject}
                    onChange={(e) => setNewDownloadSubject(e.target.value)}
                    placeholder="Add custom subject..."
                    className="flex-1 px-3 py-2 text-xs rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white font-medium"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomDownloadSubject();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={addCustomDownloadSubject}
                    className="px-3 py-2 bg-gray-800 text-white text-xs font-bold rounded-xl hover:bg-gray-700 transition-colors"
                  >
                    Add
                  </button>
                </div>

                {/* Selected Custom Subjects Tags */}
                {downloadSubjects.filter(s => !['Mathematics', 'Science', 'English', 'Social Studies', 'Hindi', 'Computer Science'].includes(s)).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1.5">
                    {downloadSubjects.filter(s => !['Mathematics', 'Science', 'English', 'Social Studies', 'Hindi', 'Computer Science'].includes(s)).map(sub => (
                      <span key={sub} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-purple-50 border border-purple-100 text-purple-700 text-xs font-semibold">
                        {sub}
                        <button type="button" onClick={() => toggleDownloadSubject(sub)} className="hover:text-purple-900 font-bold">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Exam Categories Selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-widest">
                    Configure Exam Categories Dropdown (Optional)
                  </label>
                  {downloadCategories.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setDownloadCategories([])}
                      className="text-[11px] font-semibold text-gray-400 hover:text-red-500 transition-colors"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-gray-400">
                  Optional. If empty, the Excel template will not restrict categories and your organization can omit exam categories.
                </p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {['half_yearly', 'annual'].map(cat => {
                    const isSelected = downloadCategories.includes(cat);
                    const label = cat === 'half_yearly' ? 'Half-Yearly' : 'Annual';
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleDownloadCategory(cat)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                          isSelected 
                            ? 'bg-blue-50 border-blue-200 text-blue-600 shadow-sm' 
                            : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                
                {/* Custom Category Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newDownloadCategory}
                    onChange={(e) => setNewDownloadCategory(e.target.value)}
                    placeholder="Add custom category (e.g. Unit Test 1)..."
                    className="flex-1 px-3 py-2 text-xs rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white font-medium"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomDownloadCategory();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={addCustomDownloadCategory}
                    className="px-3 py-2 bg-gray-800 text-white text-xs font-bold rounded-xl hover:bg-gray-700 transition-colors"
                  >
                    Add
                  </button>
                </div>

                {/* Selected Custom Categories Tags */}
                {downloadCategories.filter(c => !['half_yearly', 'annual'].includes(c)).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1.5">
                    {downloadCategories.filter(c => !['half_yearly', 'annual'].includes(c)).map(cat => (
                      <span key={cat} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-semibold">
                        {cat}
                        <button type="button" onClick={() => toggleDownloadCategory(cat)} className="hover:text-indigo-900 font-bold">×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-100 flex items-center justify-end gap-3 bg-white">
              <button 
                onClick={() => setShowDownloadModal(false)}
                className="px-5 py-2 text-gray-600 font-bold hover:bg-gray-50 rounded-xl transition-colors text-sm"
              >
                Cancel
              </button>
              <button 
                onClick={handleDownloadTemplate}
                className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md flex items-center gap-2 text-sm"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Delete Confirmation Modal */}
      {deleteConfirmationPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl scale-in text-center">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-100">
              <Trash2 className="w-8 h-8 animate-pulse" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">Delete Syllabus Plan?</h3>
            <p className="text-sm text-gray-500 mt-2 leading-relaxed">
              Are you sure you want to delete the academic syllabus plan for <strong>Class {deleteConfirmationPlan.class_name}</strong>? 
              This will permanently remove all subjects, teachers, chapters, and progress tracking. This action cannot be undone.
            </p>
            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => setDeleteConfirmationPlan(null)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-all text-sm"
              >
                Cancel
              </button>
              <button 
                onClick={async () => {
                  const planId = deleteConfirmationPlan.id;
                  setDeleteConfirmationPlan(null);
                  await handleDelete(planId);
                }}
                className="flex-1 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all text-sm shadow-md shadow-red-100 hover:shadow-lg"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Syllabus Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-3xl w-full max-w-lg p-6 shadow-2xl scale-in flex flex-col max-h-[85vh]">
            {/* Header */}
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center border border-indigo-100 shadow-sm">
                  <Upload className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 leading-tight">Import Syllabus Plan</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Upload filled syllabus template spreadsheet</p>
                </div>
              </div>
              <button 
                onClick={() => setShowImportModal(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
              {/* Drag & Drop Zone */}
              {!importFile && (
                <div 
                  onClick={() => document.getElementById('import-file-picker').click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer.files?.[0]) {
                      handleImportFileChange(e.dataTransfer.files[0]);
                    }
                  }}
                  className="border-2 border-dashed border-gray-300 rounded-2xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-50/20 transition-all group"
                >
                  <div className="w-14 h-14 bg-indigo-50 text-indigo-500 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                    <FileSpreadsheet className="w-7 h-7" />
                  </div>
                  <span className="text-sm font-bold text-gray-700">Click to browse or drag file here</span>
                  <span className="text-xs text-gray-400 mt-1">Excel formats (.xlsx, .xls) only</span>
                  <input 
                    id="import-file-picker"
                    type="file" 
                    className="hidden" 
                    accept=".xlsx, .xls"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleImportFileChange(e.target.files[0]);
                      }
                    }}
                  />
                </div>
              )}

              {/* Selected File Details */}
              {importFile && (
                <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-xl">
                  <div className="flex items-center gap-3 truncate">
                    <div className="w-9 h-9 bg-green-50 text-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileSpreadsheet className="w-5 h-5" />
                    </div>
                    <div className="truncate">
                      <p className="text-sm font-bold text-gray-700 truncate">{importFile.name}</p>
                      <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                        {(importFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  {!importLoading && (
                    <button 
                      onClick={() => {
                        setImportFile(null);
                        setImportValidation(null);
                        setImportError(null);
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove file"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}

              {/* Loader */}
              {importLoading && (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                  <p className="text-xs text-gray-500 font-bold mt-4 animate-pulse">
                    {importValidation ? 'Importing academic plan...' : 'Analyzing syllabus structure...'}
                  </p>
                </div>
              )}

              {/* Errors Display */}
              {importError && !importLoading && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-red-800">Validation Failures</h4>
                    <p className="text-xs text-red-600 leading-normal mt-1">{importError}</p>
                  </div>
                </div>
              )}

              {/* Validation Success & Warnings Preview */}
              {importValidation && importValidation.success && !importLoading && (
                <div className="space-y-4">
                  {/* Plan Overview Stats */}
                  {importValidation.preview && (
                    <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-indigo-700 font-extrabold text-xs uppercase tracking-wider mb-3">
                        <CheckCircle className="w-4 h-4" />
                        Template Structure Verified
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white rounded-lg p-2.5 border border-indigo-100/50 shadow-sm">
                          <span className="text-[10px] text-gray-400 uppercase font-bold">Target Class</span>
                          <p className="text-sm font-bold text-gray-800 mt-0.5">
                            Class {importValidation.preview.class_name || 'N/A'}
                          </p>
                        </div>
                        <div className="bg-white rounded-lg p-2.5 border border-indigo-100/50 shadow-sm">
                          <span className="text-[10px] text-gray-400 uppercase font-bold">Structure Summary</span>
                          <p className="text-xs font-bold text-gray-700 mt-0.5 leading-tight">
                            {importValidation.preview.subjects_count} Subjects, {importValidation.preview.chapters_count} Chapters
                          </p>
                        </div>
                      </div>

                      {importValidation.preview.exists && (
                        <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800 text-[11px] font-bold">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 animate-pulse" />
                          Note: Class {importValidation.preview.class_name} already has a plan. Importing will overwrite it.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Warnings Panel */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white">
                    <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-700">
                        Verification Alerts ({importValidation.warnings?.length || 0})
                      </span>
                      {importValidation.warnings?.length === 0 && (
                        <span className="text-[10px] bg-green-100 text-green-800 font-bold px-2 py-0.5 rounded-full">
                          All Clean
                        </span>
                      )}
                    </div>
                    <div className="p-3 max-h-[160px] overflow-y-auto space-y-2 text-xs">
                      {importValidation.warnings?.length > 0 ? (
                        importValidation.warnings.map((warning, wIdx) => (
                          <div key={wIdx} className="flex items-start gap-2 text-amber-800 font-medium">
                            <span className="text-amber-500 mt-0.5">•</span>
                            <span>{warning}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-center py-2 font-medium">
                          No empty fields, skipped lines, or structural warnings found.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Buttons */}
            <div className="flex gap-3 border-t pt-4 mt-4">
              <button 
                onClick={() => setShowImportModal(false)}
                className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-all text-sm"
              >
                Close
              </button>
              {importValidation && importValidation.success && !importError && (
                <button 
                  onClick={handleConfirmImport}
                  disabled={importLoading}
                  className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all text-sm shadow-md shadow-indigo-100"
                >
                  Confirm & Import
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademicManagement;
